import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { shoppingApi } from '../api/shopping';
import { itemsApi, type HouseholdItem } from '../api/items';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { SessionBadge } from '../components/ui/Badge';
import { SkeletonList } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { useToast } from '../components/ui/Toast';
import { ShoppingCartIcon, PlusIcon } from '../components/ui/icons';

export function ShoppingHomeScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const [storeName, setStoreName] = useState('');
  const [storeError, setStoreError] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [pendingQtyEdits, setPendingQtyEdits] = useState<Record<string, string>>({});

  const { data: activeSession, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['shopping', 'active'],
    queryFn: () => shoppingApi.getActiveSession(),
    staleTime: 10_000,
    gcTime: 60_000,
    refetchOnWindowFocus: true,
  });

  const { data: pendingItems } = useQuery({
    queryKey: ['items', { needsToBuy: true }],
    queryFn: () => itemsApi.list({ needsToBuy: true }),
    staleTime: 30_000,
  });

  const startMutation = useMutation({
    mutationFn: shoppingApi.startSession,
    onSuccess: (session) => {
      queryClient.setQueryData(['shopping', 'active'], session);
      navigate('/shopping/active');
    },
    onError: () => addToast('Erro ao iniciar compra. Tente novamente.', 'error'),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => shoppingApi.cancelSession(id),
    onSuccess: () => {
      queryClient.setQueryData(['shopping', 'active'], null);
      queryClient.invalidateQueries({ queryKey: ['shopping'] });
      addToast('Sessão cancelada.', 'info');
    },
    onError: () => addToast('Erro ao cancelar sessão.', 'error'),
  });

  const removeFromListMutation = useMutation({
    mutationFn: (id: string) => itemsApi.update(id, { needsToBuy: false, quantityNeeded: null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items', { needsToBuy: true }] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
    onError: () => addToast('Erro ao remover item.', 'error'),
  });

  const clearListMutation = useMutation({
    mutationFn: (items: HouseholdItem[]) =>
      itemsApi.bulkMarkNeeded(items.map((i) => ({ id: i.id, needsToBuy: false }))),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items', { needsToBuy: true }] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      addToast('Lista limpa.', 'info');
    },
    onError: () => addToast('Erro ao limpar lista.', 'error'),
  });

  const saveQtyMutation = useMutation({
    mutationFn: ({ id, qty }: { id: string; qty: string }) =>
      itemsApi.update(id, { quantityNeeded: qty || null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items', { needsToBuy: true }] });
    },
  });

  const handleStart = (e: React.FormEvent) => {
    e.preventDefault();
    if (!storeName.trim()) { setStoreError('Informe o nome da loja'); return; }
    setStoreError('');
    startMutation.mutate({ storeName: storeName.trim() });
  };

  const handleCancel = () => {
    if (!activeSession) return;
    setShowCancelModal(true);
  };

  const progress = activeSession
    ? `${activeSession.pickedCount ?? 0}/${activeSession.itemCount ?? 0}`
    : '';

  const pendingCount = pendingItems?.length ?? 0;

  const getQty = (item: HouseholdItem) =>
    pendingQtyEdits[item.id] !== undefined
      ? pendingQtyEdits[item.id]
      : (item.quantityNeeded ?? item.defaultQuantityToBuy ?? '1');

  return (
    <div className="flex flex-col min-h-screen pb-[calc(4rem+env(safe-area-inset-bottom)+1.5rem)]">
      <PageHeader title="Compras" onRefresh={() => refetch()} isRefreshing={isFetching} />

      <div className="flex-1 px-4 py-5 space-y-5">
        {isLoading ? (
          <SkeletonList count={3} />
        ) : activeSession ? (
          <>
            <Card className="border-l-4 border-l-emerald-500" padding={false}>
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-1">
                      Compra em andamento
                    </p>
                    <p className="text-xl font-bold text-gray-900">{activeSession.storeName}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {progress} itens &nbsp;&middot;&nbsp;
                      {activeSession.total !== null && activeSession.total !== undefined
                        ? `R$ ${Number(activeSession.total).toFixed(2).replace('.', ',')}`
                        : 'Total a calcular'}
                    </p>
                  </div>
                  <SessionBadge status="IN_PROGRESS" />
                </div>
              </div>
              <div className="flex border-t border-gray-100">
                <button
                  onClick={() => navigate('/shopping/active')}
                  className="flex-1 py-3.5 text-sm font-semibold text-emerald-600 hover:bg-emerald-50 transition-colors touch-manipulation"
                >
                  Continuar comprando
                </button>
                <div className="w-px bg-gray-100" />
                <button
                  onClick={handleCancel}
                  disabled={cancelMutation.isPending}
                  className="flex-1 py-3.5 text-sm font-medium text-red-500 hover:bg-red-50 transition-colors touch-manipulation"
                >
                  Cancelar
                </button>
              </div>
            </Card>

            <Button
              variant="primary"
              fullWidth
              size="md"
              onClick={() => navigate('/shopping/active')}
            >
              <ShoppingCartIcon size={16} />
              Ver lista de compras
            </Button>

            <ConfirmModal
              isOpen={showCancelModal}
              onClose={() => setShowCancelModal(false)}
              onConfirm={() => {
                if (activeSession) cancelMutation.mutate(activeSession.id);
                setShowCancelModal(false);
              }}
              title="Cancelar compra"
              message="Cancelar a sessão de compra atual?"
              confirmLabel="Cancelar sessão"
              confirmVariant="danger"
              loading={cancelMutation.isPending}
            />
          </>
        ) : (
          <>
            {pendingCount > 0 && (
              <Card>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-base font-bold text-gray-900">
                      Lista de compras ({pendingCount} {pendingCount === 1 ? 'item' : 'itens'})
                    </p>
                    <button
                      onClick={() => pendingItems && clearListMutation.mutate(pendingItems)}
                      disabled={clearListMutation.isPending}
                      className="text-xs text-red-500 font-medium hover:text-red-700 touch-manipulation"
                    >
                      Limpar lista
                    </button>
                  </div>

                  <div className="space-y-2">
                    {(pendingItems ?? []).map((item) => (
                      <div key={item.id} className="flex items-center gap-2">
                        <p className="flex-1 text-sm text-gray-800 truncate">{item.name}</p>
                        <input
                          type="text"
                          value={getQty(item)}
                          onChange={(e) =>
                            setPendingQtyEdits((prev) => ({ ...prev, [item.id]: e.target.value }))
                          }
                          onBlur={() => {
                            const qty = pendingQtyEdits[item.id];
                            if (qty !== undefined) {
                              saveQtyMutation.mutate({ id: item.id, qty });
                            }
                          }}
                          className="w-20 h-8 px-2 rounded-lg border border-gray-300 text-sm text-center focus:outline-none focus:ring-2 focus:ring-brand-500"
                        />
                        <button
                          onClick={() => removeFromListMutation.mutate(item.id)}
                          className="text-gray-400 hover:text-red-500 transition-colors touch-manipulation"
                          aria-label="Remover da lista"
                        >
                          <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            )}

            {pendingCount === 0 && (
              <EmptyState
                icon={
                  <div className="w-20 h-20 bg-brand-50 rounded-full flex items-center justify-center">
                    <svg width="40" height="40" fill="none" viewBox="0 0 24 24" stroke="#059669" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                }
                title="Nenhuma compra em andamento"
                description="Inicie uma nova sessão para montar sua lista e ir ao mercado."
              />
            )}

            <Card>
              <form onSubmit={handleStart} className="space-y-4">
                <p className="text-base font-bold text-gray-900">Iniciar nova compra</p>
                <Input
                  label="Nome da loja"
                  placeholder="ex: Atacadão, Carrefour, Pão de Açúcar"
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value)}
                  error={storeError}
                  name="storeName"
                  autoFocus
                />
                <Button
                  type="submit"
                  variant="primary"
                  fullWidth
                  size="md"
                  loading={startMutation.isPending}
                  title={
                    pendingCount > 0
                      ? `Iniciar compra com ${pendingCount} ${pendingCount === 1 ? 'item' : 'itens'}`
                      : 'Iniciar compra'
                  }
                  aria-label={
                    pendingCount > 0
                      ? `Iniciar compra com ${pendingCount} ${pendingCount === 1 ? 'item' : 'itens'}`
                      : 'Iniciar compra'
                  }
                >
                  <PlusIcon size={16} />
                  {pendingCount > 0 && (
                    <span className="bg-white/25 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      {pendingCount}
                    </span>
                  )}
                </Button>
              </form>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
